import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import StarRating from "../missions/components/StarRating";
import type { PayloadOptionRow } from "../../types/payload";

// Bits shared by the Payloads tab (where payloads are managed) and the
// Missions tab (where a mission's payloads are only listed).

export const EMPTY_OPTION = {
  id: null as string | null,
  name: "",
  manufacturer: "",
  summary: "",
  imageUrl: "",
  specSheetUrl: "",
  ratingResolution: 0,
  ratingEnergy: 0,
  ratingMass: 0,
  ratingCost: 0,
  ratingTrl: 0,
  orderIndex: 0,
};

export type OptionForm = typeof EMPTY_OPTION;
export type RatingKey = "ratingResolution" | "ratingEnergy" | "ratingMass" | "ratingCost" | "ratingTrl";

export function toOptionForm(o: PayloadOptionRow): OptionForm {
  return {
    id: o.id,
    name: o.name,
    manufacturer: o.manufacturer,
    summary: o.summary ?? "",
    imageUrl: o.image_url ?? "",
    specSheetUrl: o.spec_sheet_url ?? "",
    ratingResolution: o.rating_resolution,
    ratingEnergy: o.rating_energy,
    ratingMass: o.rating_mass,
    ratingCost: o.rating_cost,
    ratingTrl: o.rating_trl,
    orderIndex: o.order_index,
  };
}

// The five star ratings of a payload, labelled the way participants see
// them on the mission sheet: the first one is the payload type's own
// criterion (e.g. "Resolution [GSD]" for a camera).
export function ratingCriteria(customCriterionLabel: string): { key: RatingKey; label: string }[] {
  return [
    { key: "ratingResolution", label: customCriterionLabel || "Own criterion" },
    { key: "ratingEnergy", label: "Energy use" },
    { key: "ratingMass", label: "Mass" },
    { key: "ratingCost", label: "Cost" },
    { key: "ratingTrl", label: "TRL" },
  ];
}

export const optionRowSx = { p: 1.25, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.04)" };

// One payload with its star ratings, read-only.
export function OptionSummary(props: { option: PayloadOptionRow; customCriterionLabel: string }) {
  const form = toOptionForm(props.option);
  return (
    <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ color: "#FFFFFF", fontSize: 13, fontWeight: 700 }}>
        {props.option.name}{" "}
        {props.option.manufacturer && (
          <Box component="span" sx={{ color: "rgba(226,232,240,0.5)", fontWeight: 500 }}>({props.option.manufacturer})</Box>
        )}
      </Typography>
      {/* One criterion per line: side by side, it was unclear which stars belonged to which label. */}
      <Stack spacing={0.25}>
        {ratingCriteria(props.customCriterionLabel).map((c) => (
          <StarRating key={c.key} label={c.label} value={form[c.key]} />
        ))}
      </Stack>
    </Stack>
  );
}
